import { Moment } from 'moment-timezone';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ExperienceDateType, FeatureType } from '@apply/constants';
import { IAddress } from '../cross';
import { logApplicationInsightsTrace } from '../../../../apps/admin/src/common/SharedFunctions';

const prefix = 'user/';

export const UPDATE_USER_APPLICATIONS = `${prefix}UPDATE_USER_APPLICATIONS`;
export const UPDATE_USER_NOTICES = `${prefix}UPDATE_USER_NOTICES`;
export const UPDATE_USER_NOTICE_COUNTS = `${prefix}UPDATE_USER_NOTICE_COUNTS`;
export const UPDATE_USER_NOTICE = `${prefix}UPDATE_USER_NOTICE`;
export const SIGN_USER_NOTICE = `${prefix}SIGN_USER_NOTICE`;
export const UPDATE_USER_EXPERIENCES = `${prefix}UPDATE_USER_EXPERIENCES`;

export type FeatureStatus = { featureId: FeatureType; isEnabled: boolean };

export type User = {
    userId: string;
    email: string;
    firstName: string;
    middleName: string;
    lastName: string;
    secondaryEmail: string;
    mobilePhone: string;
    workPhone: string;
    requireTermsheet: boolean;
    requireValuationFee: boolean;
    requireUnderwritingFee: boolean;
    isGeneralContractor: boolean;
    isRegistered: boolean;
    featureList: [FeatureStatus];
};

export type ApplyUser = {
    eSignAgreementDate: Date;
};

export type Authentication = {
    username: string;
    token: string;
    refreshToken: string;
    expiry: number; // clock ticks a la Date.now()
    user: User;
    applicant: ApplyUser;
    isLocEnabled: boolean;
    requiresESign: boolean;
    requiresAdminTermsESign: boolean;
    featureList: [FeatureStatus];
};

export enum ValidateEmailResultStatus {
    Available = 0,
    Unavailable = 1,
    AccountExists = 2,
}

export enum UserType {
    Principal = 0,
    Broker = 1,
    Admin = 2,
}

export type ValidateEmailResult = {
    status: ValidateEmailResultStatus;
    baseUrl: string;
};

export type ValidateEmailResponse = {
    result?: ValidateEmailResult;
    statusCode: number;
};

export enum RegisterUserResultStatus {
    RequestSuccessful = 0,
    EmailUnavailable = 1,
    AccountExists = 2,
    InvalidGCRegistrationUpgrade = 3,
}

export type RegisterUserResult = {
    status: RegisterUserResultStatus;
    baseUrl: string;
};

export type RegisterUserResponse = {
    result?: RegisterUserResult;
    statusCode: number;
};

export const STORAGE_KEY = 'apply-auth';

const RELEASE_VERSION = 2;

const reactPlugin = new ReactPlugin();

export const appInsightsApply = import.meta.env.VITE_APPINSIGHTS_CLIENT_APPLY;
const ai = new ApplicationInsights({
    config: {
        instrumentationKey: appInsightsApply,
        enableAutoRouteTracking: true,
        extensions: [reactPlugin],
    },
});

export const store = (auth: Authentication): void => {
    const object = {
        value: JSON.stringify(auth),
        timestamp: new Date().getTime(),
        version: RELEASE_VERSION,
    };
    logApplicationInsightsTrace('Auth store:', object);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(object));
};

export const retrieve = (): Authentication => {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
        const stackTrace = Error().stack;
        logApplicationInsightsTrace('No Auth data was found in local storage', { stackTrace });
        return undefined;
    } else {
        try {
            const object = JSON.parse(data);

            if (object?.version < RELEASE_VERSION) {
                logApplicationInsightsTrace('Auth cleared due to release version');
                clear();
                return;
            }

            const tsDate = new Date(object.timestamp);
            const nowDate = new Date();
            // Force 12 hour clear of the token from the user
            tsDate.setTime(tsDate.getTime() + 12 * 60 * 60 * 1000);
            if (nowDate > tsDate) {
                logApplicationInsightsTrace('Auth cleared due 12 hour limit');
                clear();
            } else {
                logApplicationInsightsTrace('Auth information found');
                return JSON.parse(object.value) as Authentication;
            }
        } catch (e) {
            logApplicationInsightsTrace('Auth cleared due exception in type.ts (see exception)', e);

            clear();
        }
        logApplicationInsightsTrace('Reached end of Auth retrieve.');
    }
};

export const clear = (): void => {
    localStorage.removeItem(STORAGE_KEY);
    const stackTrace = Error().stack;
    logApplicationInsightsTrace(`Clear of ${STORAGE_KEY} occured.`, { stackTrace });
};

export const isExpired = (auth: Authentication): boolean => {
    if (!auth || auth.expiry <= Date.now()) {
        return true;
    }
    return false;
};

export const isAuthenticated = (auth: Authentication | undefined) => !!auth?.token;

export const isRegisteredUser = (auth: Authentication | undefined) => {
    if (!auth) {
        return false;
    }
    return true;
};

export const isLocEnabled = (auth: Authentication | undefined) => {
    return auth && auth.isLocEnabled;
};

export const requiresESign = (auth: Authentication | undefined) => {
    return auth && auth.requiresESign;
};

export const isFeatureEnabled = (user: User, featureType: FeatureType) => {
    if (!user || !user.featureList) {
        return false;
    }
    const feature = user.featureList.find((f) => f.featureId === featureType);
    return feature && feature.isEnabled;
};

export type UserApplication = {
    apiId: string;
    complete: boolean;
    lastModified: Moment;
    propertyCount: number;
    propertyNames: string;
    extendedPropertyNames: string;
};

export enum NoticeType {
    AppraisalNotice = 0,
    AppraisalReport = 1,
    IncompleteFile = 2,
    AdverseAction = 3,
    FloodNotice = 4,
    PostClosingCertificationNotice = 5,
}

export enum ESignType {
    NotRequired = 0,
    Optional = 1,
    Required = 2,
}

export type UserNoticeCounts = {
    total: number;
    unread: number;
};

export type NoticeFile = {
    name: string;
    url: string;
    address: string;
};

export type UserNoticeLean = {
    id: number;
    noticeTypeId: NoticeType;
    noticeDate: Moment;
    isRead: boolean;
    eSignDate?: Moment | undefined | null;
    eSignName: string;
    eSignTypeId: ESignType;
    canDelete: boolean;
    noticeFile: NoticeFile;
    signedFile: NoticeFile;
    isManual: boolean;
    addresses: [IAddress];
};

export type Font = {
    id: number;
    name: string;
};

export type UserNotice = UserNoticeLean & {
    content: string;
    eSignName: string;
    eSignFont?: Font;
};

export type SignUserNotice = {
    noticeId: number;
    fontId: number;
};

export type TempFileUploadResult = {
    tempName: string;
    fileName: string;
    url;
    string;
};

export enum ExperienceType {
    NonBfi = 0,
    BfiRepaid = 1,
    BfiOutstanding = 2,
}

export enum ExperienceProjectType {
    Construction = 0,
    FixFlipRehab = 1,
    Rental = 2,
    BuildToRent = 3,
    PersonalResidence = 4,
}

export enum ExperiencePropertyType {
    DetachedHouse = 0,
    Townhouse = 1,
    TwoToFourUnitStructure = 2,
    CondoUnit = 3,
    FivePlusMultifamily = 4,
    ResidentialMixedUse = 5,
    Commercial = 6,
}

export enum ExperienceTwoToFourUnitType {
    Two = 0,
    Three = 1,
    Four = 2,
}

export type UserExperience = UserExperienceLean & {
    content: string;
};

export type UserExperienceLean = {
    id: number;
    principalId: number;
    projectTypeId?: ExperienceProjectType;
    experienceTypeId: ExperienceType;
    propertyTypeId?: ExperiencePropertyType;
    twoToFourUnitTypeId?: ExperienceTwoToFourUnitType;
    lender: string;
    address: IAddress;
    stillOwn: boolean;
    dateSold?: Date;
    dateSoldTypeId?: ExperienceDateType;
    salesPrice?: number;
    budget: number;
    unitCount: number;
    verificationStatusTypeId?: number;
    isVerified: boolean;
    commercialConsiderationAcknowledged?: boolean;
    homeConstructedAfterPurchase?: boolean;
};

export type UserExperienceGridRecord = {
    id?: number;
    experienceTypeId: ExperienceType;
    projectTypeId: ExperienceProjectType;
    verificationStatusTypeId?: number;
    isVerified?: boolean;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    county?: string;
    rowNumber?: number;
};

export type UserState = {
    completedApplications: UserApplication[];
    inProgressApplications: UserApplication[];
    userNotices: UserNoticeLean[];
    noticeCounts: UserNoticeCounts;
    userNotice: UserNotice;
    signedNotice: SignUserNotice;
    activeExperiences: UserExperienceGridRecord[];
    unverifiableExperiences: UserExperienceGridRecord[];
    applicableProvidedExperienceCount: number;
};

export const userInitialState: UserState = {
    completedApplications: [],
    inProgressApplications: [],
    userNotices: [],
    noticeCounts: { total: 0, unread: 0 },
    userNotice: undefined,
    signedNotice: undefined,
    activeExperiences: [],
    unverifiableExperiences: [],
    applicableProvidedExperienceCount: 0,
};
